import type { Category } from "./types";
import { getCategoryIconComponent } from "./categoryIcons";
import { appToday } from "./devDate";

export function money(value: string | number | null | undefined) {
  const amount = Number(value ?? 0);
  return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function currencySymbol(currency?: string | null) {
  const symbols: Record<string, string> = {
    EUR: "€",
    USD: "$",
    GBP: "£",
    JPY: "¥",
    CHF: "CHF",
    CAD: "C$",
    AUD: "A$",
    PLN: "zł",
    RUB: "₽",
  };
  const code = String(currency || "").toUpperCase();
  return symbols[code] || code || "";
}

export function dateInputValue(value: string | Date | null | undefined) {
  const date = value ? new Date(value) : appToday();
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function categoryDisplayName(category: Category, _categories?: Category[]) {
  return category.name;
}

function compareCategoryLabels(left: Category, right: Category, categories: Category[], indexById: Map<string, number>) {
  const labelOrder = categoryDisplayName(left, categories).localeCompare(categoryDisplayName(right, categories));
  if (labelOrder !== 0) return labelOrder;
  return (indexById.get(left.id) ?? 0) - (indexById.get(right.id) ?? 0);
}

export function sortCategoriesHierarchically(categories: Category[], allCategories: Category[] = categories) {
  const indexById = new Map(categories.map((category, index) => [category.id, index]));
  const includedIds = new Set(categories.map((category) => category.id));
  const childrenByParent = new Map<string, Category[]>();
  const topLevel: Category[] = [];

  for (const category of categories) {
    if (category.parent_id && includedIds.has(category.parent_id)) {
      if (!childrenByParent.has(category.parent_id)) childrenByParent.set(category.parent_id, []);
      childrenByParent.get(category.parent_id)!.push(category);
    } else {
      topLevel.push(category);
    }
  }

  const sorted: Category[] = [];
  for (const category of topLevel.sort((left, right) => compareCategoryLabels(left, right, allCategories, indexById))) {
    sorted.push(category);
    sorted.push(...(childrenByParent.get(category.id) || []).sort((left, right) => compareCategoryLabels(left, right, allCategories, indexById)));
  }
  return sorted;
}

export function CategorySelectOption({
  category,
  indent = true,
  showIcon = true,
}: {
  category: Category;
  indent?: boolean;
  showIcon?: boolean;
}) {
  return (
    <span className={`records-select-option${indent && category.parent_id ? " category-child" : ""}`}>
      {showIcon ? <CategoryIcon icon={category.icon} color={category.color} /> : null}
      <span>{category.name}</span>
    </span>
  );
}

export function CategoryIcon({ icon, color }: { icon?: string | null; color?: string | null }) {
  const Icon = getCategoryIconComponent(icon);
  return (
    <span className="category-icon" style={{ backgroundColor: `${color || "#dde3ea"}22` }}>
      <Icon aria-hidden="true" />
    </span>
  );
}
